'use client';

import { useState, useEffect } from 'react';
import { User, Mail, Phone, Camera, Save, MessageSquare, Moon, Sun, Palette } from 'lucide-react';
import Image from 'next/image';
import { useTheme } from '@/core/providers/ThemeProvider';
import { useAppDispatch, useAppSelector } from '@/core/store/hooks';
import { reduxLogger } from '@/core/utils/logger';
import { 
  setMessageFontSize, 
  FontSizeOption 
} from '../../store/settingsSlice';
import {
  useGetUserProfileQuery,
  useUpdateUserProfileMutation,
  useUploadProfileImageMutation,
  UpdateUserProfilePayload
} from '@/api/features/users';
import { showToast, extractErrorMessage } from '@/shared/utils/toast';
import { RadioButton } from '@/shared/components/ui/RadioButton';
import { ImageUploadButton } from '@/core/components/ui/ImageUploadButton';
import { PersonalInformationHeader } from './PersonalInformationHeader';

const PersonalInformation = () => {
  const { theme, setMode, isDarkMode } = useTheme();
  const dispatch = useAppDispatch();
  // Auth no longer needed - profile updates automatically refresh
  
  // RTK QUERY: Single source of truth for server data
  const { data: userProfile } = useGetUserProfileQuery(undefined);
  const [updateUserProfileMutation, { isLoading: isUpdatingProfile }] = useUpdateUserProfileMutation();
  const [uploadProfileImageMutation, { isLoading: isUploadingImage }] = useUploadProfileImageMutation();

  // SETTINGS STATE: Keep for message font size (legitimate UI preference)
  const messageFontSize = useAppSelector(state => state.settings.messageFontSize);
  
  // LOCAL STATE: Form and UI state only
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [avatarType, setAvatarType] = useState<'color' | 'image'>('color');
  const [avatarColor, setAvatarColor] = useState('');
  const [displayImageUrl, setDisplayImageUrl] = useState<string | null>(null);

  // Display settings state
  const [selectedFontSize, setSelectedFontSize] = useState<FontSizeOption>(messageFontSize);

  // Get unique colors from theme
  const colorOptions = theme.avatarColors;
  
  // INITIALIZE: Load data from RTK Query (single source)
  // Only update local form state when server data changes
  useEffect(() => {
    if (userProfile) {
      const firstName = userProfile.firstName || '';
      const lastName = userProfile.lastName || '';
      queueMicrotask(() => {
        setName(`${firstName} ${lastName}`.trim());
        setEmail(userProfile.email || '');
        setPhone(userProfile.phoneNumber || '');

        // REAL DATA ONLY: Avatar state reflects server data
        if (userProfile.profileImageUrl) {
          setAvatarType('image');
          setDisplayImageUrl(userProfile.profileImageUrl);
          setAvatarColor(''); // Clear color if image is primary
        } else if (userProfile.avatarColor) {
          setAvatarType('color');
          setAvatarColor(userProfile.avatarColor);
          setDisplayImageUrl(null); // Clear image URL if color is primary
        } else {
          // Default if nothing is set
          setAvatarType('color');
          setAvatarColor(colorOptions[0]); // Default to first color option
          setDisplayImageUrl(null);
        }
      });
    }
    
    // Load display settings
    queueMicrotask(() => {
      setSelectedFontSize(messageFontSize);
    });
  }, [userProfile, messageFontSize, colorOptions]);
  
  // Handle profile image upload
  const handleProfileImageUpload = async (file: File) => {
    if (!userProfile?.id) return;

    // Image is already cropped to a centered square on the client
    const result = await uploadProfileImageMutation({
      userId: userProfile.id,
      file
    }).unwrap();

    // Immediately display the uploaded image (don't wait for refetch)
    setDisplayImageUrl(result.profileImageUrl);
    setAvatarType('image');

    reduxLogger.info('Image uploaded successfully:', result.profileImageUrl);
  };

  // HYBRID SAVE: RTK Query for server, settings dispatch for UI prefs, no userProfile sync
  const handleSave = async () => {
    let hasPersonalInfoChanges = false;
    let hasDisplayChanges = false;

    // Handle personal information changes
    const backendPayload: UpdateUserProfilePayload = {};

    // Name
    const firstName = userProfile?.firstName || '';
    const lastName = userProfile?.lastName || '';
    const currentFullName = `${firstName} ${lastName}`.trim();
    if (name.trim() !== currentFullName) {
      const nameParts = name.trim().split(/\s+/);
      backendPayload.firstName = nameParts[0] || '';
      backendPayload.lastName = nameParts.slice(1).join(' ') || '';
      hasPersonalInfoChanges = true;
    }

    // Phone
    if (phone !== (userProfile?.phoneNumber || '')) {
      backendPayload.phoneNumber = phone;
      hasPersonalInfoChanges = true;
    }
    
    // Avatar - only update if switching to color mode or if already saved to Cloudinary
    if (avatarType === 'color') {
      if (avatarColor !== userProfile?.avatarColor || userProfile?.profileImageUrl) {
        backendPayload.avatarColor = avatarColor;
        backendPayload.profileImageUrl = null; // Clear image URL
        hasPersonalInfoChanges = true;
      }
    }
    // Note: Image uploads are handled directly in handleImageUpload, not in save

    // Handle display settings changes
    if (selectedFontSize !== messageFontSize) {
      hasDisplayChanges = true;
    }

    if (!hasPersonalInfoChanges && !hasDisplayChanges) {
      showToast.success('Profile saved');
      return;
    }

    try {
      // Update personal information if there are changes
      if (hasPersonalInfoChanges) {
        if (!userProfile?.id) {
          reduxLogger.error('User ID is missing, cannot update profile.');
          showToast.error('Unable to save', 'User ID is missing');
          return;
        }

        // REAL UPDATE: Wait for server response before showing success
        await updateUserProfileMutation({
          id: userProfile.id,
          data: backendPayload
        }).unwrap();

        reduxLogger.info('Profile updated successfully on server');
      }

      // UI preferences update (legitimate settings Redux usage)
      if (hasDisplayChanges) {
        dispatch(setMessageFontSize(selectedFontSize));
      }

      // Only show success after server confirms
      showToast.success('Profile updated successfully');

    } catch (error) {
      reduxLogger.error('Failed to update profile:', error);
      showToast.error('Failed to update profile', extractErrorMessage(error));
    }
  };

  return (
    <div className="max-w-6xl mx-auto space-y-6" data-testid="settings-profile-section">
      <PersonalInformationHeader />

      <div className="bg-card-background rounded-lg shadow-sm border border-border p-4 sm:p-6">
        <div className="flex items-center mb-4 sm:mb-6">
          <User className="h-5 w-5 text-text-secondary mr-2" />
          <h2 className="text-lg font-medium text-text-primary">Personal Information</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          <AvatarSection
            avatarType={avatarType} setAvatarType={setAvatarType}
            avatarColor={avatarColor} setAvatarColor={setAvatarColor}
            displayImageUrl={displayImageUrl} name={name}
            userProfile={userProfile} theme={theme} colorOptions={colorOptions}
            handleProfileImageUpload={handleProfileImageUpload}
            isUploadingImage={isUploadingImage}
          />
          <PersonalDetailsForm
            name={name} setName={setName} email={email}
            phone={phone} setPhone={setPhone}
          />
        </div>
      </div>

      <DisplaySettingsCard
        isDarkMode={isDarkMode} setMode={setMode}
        selectedFontSize={selectedFontSize} setSelectedFontSize={setSelectedFontSize}
      />

      <div className="flex justify-end">
        <button onClick={handleSave} disabled={isUpdatingProfile}
          className="inline-flex items-center px-4 py-2 bg-primary text-white text-sm font-medium rounded-lg hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          data-testid="settings-profile-save-button">
          {isUpdatingProfile ? (
            <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>Saving...</>
          ) : (
            <><Save size={16} className="mr-2" />Save Changes</>
          )}
        </button>
      </div>
    </div>
  );
};

// --- Sub-component: Avatar section ---
function AvatarSection({
  avatarType, setAvatarType, avatarColor, setAvatarColor, displayImageUrl,
  name, userProfile, theme, colorOptions, handleProfileImageUpload, isUploadingImage,
}: {
  avatarType: 'color' | 'image';
  setAvatarType: (v: 'color' | 'image') => void;
  avatarColor: string;
  setAvatarColor: (v: string) => void;
  displayImageUrl: string | null;
  name: string;
  userProfile: { firstName?: string; lastName?: string } | undefined;
  theme: { primary: string; avatarColors: string[] };
  colorOptions: string[];
  handleProfileImageUpload: (file: File) => Promise<void>;
  isUploadingImage: boolean;
}) {
  return (
    <div className="md:col-span-1 flex flex-col items-center space-y-4">
      <div className="mb-4">
        {avatarType === 'color' ? (
          <div className="w-24 h-24 sm:w-32 sm:h-32 rounded-full flex items-center justify-center text-white text-xl sm:text-3xl font-semibold border-2 border-border"
            style={{ backgroundColor: avatarColor || 'transparent' }}>
            {userProfile ? ((userProfile.firstName?.[0] || '') + (userProfile.lastName?.[0] || '')).toUpperCase() : ''}
          </div>
        ) : (
          <div className="relative w-24 h-24 sm:w-32 sm:h-32">
            {displayImageUrl ? (
              <Image src={displayImageUrl} alt={name} width={128} height={128} className="rounded-full object-cover w-full h-full border-2 border-border" />
            ) : (
              <div className="w-full h-full rounded-full bg-surface flex items-center justify-center border-2 border-border">
                <Camera size={32} className="text-text-secondary" />
              </div>
            )}
          </div>
        )}
      </div>
      <div className="w-full space-y-4 flex flex-col items-center">
        <div className="space-y-3">
          <RadioButton name="avatar-type" value="color" checked={avatarType === 'color'} onChange={() => setAvatarType('color')} label="Use color avatar" />
          <div>
            <RadioButton name="avatar-type" value="image" checked={avatarType === 'image'} onChange={() => setAvatarType('image')} label="Use custom image" />
            {avatarType === 'image' && <p className="text-xs text-text-secondary ml-6 mt-1">Must be .png, .jpg, or .webp</p>}
          </div>
        </div>
        {avatarType === 'color' && (
          <div className="flex flex-wrap gap-2 justify-center mt-4">
            {colorOptions.map(color => (
              <button key={color}
                className={`w-6 h-6 sm:w-8 sm:h-8 rounded-full ${avatarColor === color ? 'outline outline-2 outline-offset-2' : ''}`}
                style={{ backgroundColor: color, outlineColor: avatarColor === color ? theme.primary : 'transparent' }}
                onClick={() => setAvatarColor(color)} aria-label={`Select color ${color}`} />
            ))}
          </div>
        )}
        {avatarType === 'image' && (
          <div className="w-full mt-4">
            <ImageUploadButton onUpload={handleProfileImageUpload}
              onSuccess={() => showToast.success('Profile image updated')}
              onError={(error) => { reduxLogger.error('Image upload error:', error); showToast.error('Failed to upload image', extractErrorMessage(error)); }}
              disabled={isUploadingImage} showCropper={true} cropperCircular={true} maxWidth={400} maxHeight={400} quality={0.9} />
          </div>
        )}
      </div>
    </div>
  );
}

// --- Sub-component: Personal details form ---
function PersonalDetailsForm({
  name, setName, email, phone, setPhone,
}: {
  name: string; setName: (v: string) => void; email: string;
  phone: string; setPhone: (v: string) => void;
}) {
  return (
    <div className="md:col-span-1 space-y-4">
      <div className="space-y-3">
        <label className="block text-sm font-medium text-text-primary mb-2 flex items-center">
          <User size={16} className="mr-2 text-text-secondary" />Full Name
        </label>
        <input type="text" className="input" value={name} onChange={(e) => setName(e.target.value)}
          placeholder="Enter your full name" data-testid="settings-profile-name-input" />
      </div>
      <div className="space-y-3">
        <label className="block text-sm font-medium text-text-primary mb-2 flex items-center">
          <Mail size={16} className="mr-2 text-text-secondary" />Email Address
        </label>
        <input type="email" className="input bg-surface/50 cursor-not-allowed" value={email} disabled
          placeholder="Enter your email address" data-testid="settings-profile-email-input" />
        <p className="text-xs text-text-secondary">Email address cannot be changed</p>
      </div>
      <div className="space-y-3">
        <label className="block text-sm font-medium text-text-primary mb-2 flex items-center">
          <Phone size={16} className="mr-2 text-text-secondary" />Phone Number
        </label>
        <input type="tel" className="input" value={phone} onChange={(e) => setPhone(e.target.value)}
          placeholder="Enter your phone number" data-testid="settings-profile-phone-input" />
      </div>
    </div>
  );
}

// --- Sub-component: Display settings card ---
function DisplaySettingsCard({
  isDarkMode, setMode, selectedFontSize, setSelectedFontSize,
}: {
  isDarkMode: boolean; setMode: (m: 'light' | 'dark') => void;
  selectedFontSize: FontSizeOption; setSelectedFontSize: (v: FontSizeOption) => void;
}) {
  return (
    <div className="bg-card-background rounded-lg shadow-sm border border-border p-4 sm:p-6">
      <div className="flex items-center mb-4 sm:mb-6">
        <Palette className="h-5 w-5 text-text-secondary mr-2" />
        <h2 className="text-lg font-medium text-text-primary">Display Settings</h2>
      </div>
      <div className="space-y-8">
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center">
                {isDarkMode ? <Moon className="h-4 w-4 text-primary" /> : <Sun className="h-4 w-4 text-primary" />}
              </div>
              <div>
                <h3 className="text-base font-medium text-text-primary">Theme Mode</h3>
                <p className="text-sm text-text-secondary">Switch between light and dark themes</p>
              </div>
            </div>
            <button onClick={() => setMode(isDarkMode ? 'light' : 'dark')}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${isDarkMode ? 'bg-primary' : 'bg-gray-200'}`}
              type="button" role="switch" aria-checked={isDarkMode}>
              <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${isDarkMode ? 'translate-x-6' : 'translate-x-1'}`} />
            </button>
          </div>
        </div>
        <div className="space-y-4">
          <div className="flex items-center space-x-3 mb-4">
            <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center">
              <MessageSquare className="h-4 w-4 text-primary" />
            </div>
            <div>
              <h3 className="text-base font-medium text-text-primary">Text Size</h3>
              <p className="text-sm text-text-secondary">Choose a comfortable text size for messages and interface text</p>
            </div>
          </div>
          <div className="space-y-4 pl-11">
            <div className="space-y-2">
              <div className="flex items-center">
                <RadioButton name="font-size" value="medium" checked={selectedFontSize === 'medium'} onChange={() => setSelectedFontSize('medium')} label="" />
                <span className="text-base ml-2">Standard</span>
              </div>
              <div className="pl-6"><span className="text-sm text-text-secondary">Default size that works well for most users</span></div>
            </div>
            <div className="space-y-2">
              <div className="flex items-center">
                <RadioButton name="font-size" value="large" checked={selectedFontSize === 'large'} onChange={() => setSelectedFontSize('large')} label="" />
                <span className="text-lg ml-2">Large</span>
              </div>
              <div className="pl-6"><span className="text-sm text-text-secondary">Larger text for better readability</span></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default PersonalInformation; 